import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { signOut, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, collection, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import { db, firestorePathId, auth } from '../services/firebase';
import bcrypt from 'bcryptjs'; 

const AuthContext = createContext();
const INACTIVITY_TIME_MS = 15 * 60 * 1000;

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [vinculos, setVinculos] = useState([]);
    const [vinculoSelecionado, setVinculoSelecionado] = useState(null);
    const [solicitacaoConfig, setSolicitacaoConfig] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    
    // loading: Controla APENAS o carregamento inicial (boot) da aplicação.
    const [loading, setLoading] = useState(true);
    
    const [sessionTimeLeft, setSessionTimeLeft] = useState(INACTIVITY_TIME_MS);
    const [authError, setAuthError] = useState(null);
    
    // Silenciador de Eventos para evitar conflito durante login manual
    const loginInProgress = useRef(false);

    const [unidadesDicionario, setUnidadesDicionario] = useState({});

    const clearAuthError = useCallback(() => {
        setAuthError(null);
    }, []);

    const setPasswordChanged = useCallback(() => {
        setUser(currentUser => {
            if (currentUser) {
                const updatedUser = { ...currentUser, mustChangePassword: false };
                const sessionData = JSON.parse(sessionStorage.getItem('servidorSessionData')) || {};
                sessionData.user = updatedUser;
                sessionStorage.setItem('servidorSessionData', JSON.stringify(sessionData));
                return updatedUser;
            }
            return null;
        });
    }, []);

    // --- LOGOUT COM MENSAGEM PADRÃO ---
    const logout = useCallback(async (message) => {
        // Se estivermos no meio de um login (buffer de segurança), ignoramos o logout automático
        if (loginInProgress.current) {
            console.log("Tentativa de logout bloqueada: Login em andamento ou buffer de transição.");
            return;
        }

        sessionStorage.removeItem('servidorSessionData');
        
        try {
            await signOut(auth);
        } catch (error) {
            console.error("Erro ao fazer signOut do Firebase:", error);
        } finally {
            setUser(null);
            setVinculos([]);
            setVinculoSelecionado(null);
            setSolicitacaoConfig(null);
            setUnidadesDicionario({});
            setIsAuthenticated(false);
            
            const savedMunicipioId = localStorage.getItem('sisfol_municipio_persist');
            let destination = '/login';
            
            const params = new URLSearchParams();
            const msgFinal = message || "Sessão encerrada com segurança.";
            params.append('message', msgFinal);
            
            if (savedMunicipioId) params.append('municipioId', savedMunicipioId);
            
            destination += `?${params.toString()}`;
            
            window.location.href = destination;
        }
    }, []);
        
    const selecionarVinculo = useCallback((vinculo) => {
        setVinculoSelecionado(vinculo);
        const sessionData = JSON.parse(sessionStorage.getItem('servidorSessionData')) || {};
        sessionData.vinculoSelecionado = vinculo;
        sessionStorage.setItem('servidorSessionData', JSON.stringify(sessionData));
    }, []);

    const limparVinculoSelecionado = useCallback(() => {
        setVinculoSelecionado(null);
        const sessionData = JSON.parse(sessionStorage.getItem('servidorSessionData')) || {};
        delete sessionData.vinculoSelecionado;
        sessionStorage.setItem('servidorSessionData', JSON.stringify(sessionData));
    }, []);

    const loadUnidadesGestoras = async (municipioId) => {
        try {
            const response = await fetch(`/data/unidgest.tsv`);
            if (response.ok) {
                const text = await response.text();
                const lines = text.split('\n');
                const dict = {};
                lines.forEach(line => {
                    const parts = line.split('\t');
                    if (parts.length >= 2) {
                        const id = parts[0].trim();
                        const nome = parts[1].trim();
                        if (id && nome) dict[id] = nome;
                    }
                });
                setUnidadesDicionario(dict);
                return dict;
            } else {
                return {};
            }
        } catch (e) {
            return {};
        }
    };

    const enrichVinculo = (vinculo, dict) => {
        const idGestora = String(vinculo.unidgest_id);
        const nomeLotacao = dict[idGestora] || vinculo.lotacao || idGestora || 'Não identificada';
        
        return {
            ...vinculo,
            lotacao: nomeLotacao,
            secretaria: nomeLotacao
        };
    };

    useEffect(() => {
        if (!isAuthenticated) return;
        let inactivityTimer, intervalTimer;
        const resetTimer = () => {
            clearTimeout(inactivityTimer);
            clearInterval(intervalTimer);
            setSessionTimeLeft(INACTIVITY_TIME_MS);
            inactivityTimer = setTimeout(() => logout('Sessão expirada por inatividade.'), INACTIVITY_TIME_MS);
            intervalTimer = setInterval(() => setSessionTimeLeft(prevTime => prevTime > 0 ? prevTime - 1000 : 0), 1000);
        };
        const events = ['mousemove', 'keydown', 'mousedown', 'touchstart'];
        events.forEach(event => window.addEventListener(event, resetTimer));
        resetTimer();
        return () => {
            clearTimeout(inactivityTimer);
            clearInterval(intervalTimer);
            events.forEach(event => window.removeEventListener(event, resetTimer));
        };
    }, [isAuthenticated, logout]);

    // O "VIGIA" DO FIREBASE
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
            
            // --- BLOQUEIO DE SEGURANÇA ---
            if (loginInProgress.current) {
                console.log("Login manual/transição em andamento. Ignorando evento do Firebase.");
                return;
            }
            // -----------------------------

            if (authUser) {
                const sessionDataJSON = sessionStorage.getItem('servidorSessionData');
                if (sessionDataJSON) {
                    const sessionData = JSON.parse(sessionDataJSON);
                    if (sessionData.user && sessionData.user.uid === authUser.uid) {
                        
                        const userFromSession = sessionData.user;
                        let sessionDataNeedsUpdate = false;
    
                        if (userFromSession.cpf && (userFromSession.cpf.includes('.') || userFromSession.cpf.includes('-'))) {
                            userFromSession.cpf = userFromSession.cpf.replace(/[.-]/g, '');
                            sessionDataNeedsUpdate = true;
                        }

                        let currentDict = unidadesDicionario;
                        if (Object.keys(currentDict).length === 0 && userFromSession.municipioId) {
                            currentDict = await loadUnidadesGestoras(userFromSession.municipioId);
                        }
    
                        const vinculosEnriquecidos = (sessionData.vinculos || []).map(v => enrichVinculo(v, currentDict));
                        let vinculoSelEnriquecido = sessionData.vinculoSelecionado ? enrichVinculo(sessionData.vinculoSelecionado, currentDict) : null;

                        setUser(userFromSession);
                        setVinculos(vinculosEnriquecidos);
                        setVinculoSelecionado(vinculoSelEnriquecido);
                        setSolicitacaoConfig(sessionData.solicitacaoConfig || null); 
                        setIsAuthenticated(true);
    
                        if (sessionDataNeedsUpdate) {
                            const updatedSessionData = { ...sessionData, user: userFromSession, vinculos: vinculosEnriquecidos, vinculoSelecionado: vinculoSelEnriquecido };
                            sessionStorage.setItem('servidorSessionData', JSON.stringify(updatedSessionData));
                        }
                    } else { 
                        logout('Sessão inválida.'); 
                    }
                } else {
                    // Se não achou dados, mas tem auth, tenta logout.
                    // A proteção 'loginInProgress' impedirá isso de acontecer logo após o login no mobile.
                    logout();
                }
            } else {
                sessionStorage.removeItem('servidorSessionData');
                setUser(null);
                setVinculos([]);
                setVinculoSelecionado(null);
                setSolicitacaoConfig(null);
                setUnidadesDicionario({});
                setIsAuthenticated(false);
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, [logout, unidadesDicionario]);

    // Listeners de dados (Permissões e Config)
    useEffect(() => {
        if (!isAuthenticated || !user?.municipioId) return;
        const municipalityDocRef = doc(db, `artifacts/${firestorePathId}/public/data/municipalities/${user.municipioId}`);
        const unsubscribePermissions = onSnapshot(municipalityDocRef, (docSnap) => {
            if (docSnap.exists()) {
                const newPermissions = docSnap.data().serverPortalPermissions || {};
                const newLogoUrl = docSnap.data().logoUrl || '';
                setUser(currentUser => {
                    if (!currentUser || (JSON.stringify(currentUser.serverPortalPermissions) === JSON.stringify(newPermissions) && currentUser.logoUrl === newLogoUrl)) return currentUser;
                    const updatedUser = { ...currentUser, serverPortalPermissions: newPermissions, logoUrl: newLogoUrl };
                    const sessionData = JSON.parse(sessionStorage.getItem('servidorSessionData')) || {};
                    sessionData.user = updatedUser;
                    sessionStorage.setItem('servidorSessionData', JSON.stringify(sessionData));
                    return updatedUser;
                });
            }
        });
        return () => unsubscribePermissions();
    }, [isAuthenticated, user?.municipioId]);

    useEffect(() => {
        if (!isAuthenticated || !user?.municipioId) return;
        const configDocRef = doc(db, `artifacts/${firestorePathId}/public/data/municipalities/${user.municipioId}/configuracoes/solicitacoesServidor`);
        const unsubscribeConfig = onSnapshot(configDocRef, (docSnap) => {
            const configData = docSnap.exists() ? docSnap.data() : {};
            setSolicitacaoConfig(configData);
            const sessionData = JSON.parse(sessionStorage.getItem('servidorSessionData')) || {};
            sessionData.solicitacaoConfig = configData;
            sessionStorage.setItem('servidorSessionData', JSON.stringify(sessionData));
        }, (error) => { setSolicitacaoConfig({}); });
        return () => unsubscribeConfig();
    }, [isAuthenticated, user?.municipioId]);

    // --- FUNÇÃO DE LOGIN MANUAL ---
    const login = async (cpf, password, municipioId) => {
        // 1. Ativa o bloqueio de eventos
        loginInProgress.current = true;
        setAuthError(null);
    
        try {
            await signOut(auth);
    
            const cleanedCpf = cpf.replace(/[.-]/g, '');
            const credentialPath = `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/Servidoresacesso/${cleanedCpf}`;
            
            const credentialDocRef = doc(db, credentialPath);
            const credentialSnap = await getDoc(credentialDocRef);
    
            if (credentialSnap.exists()) {
                const credentialData = credentialSnap.data();
                
                let isPasswordValid = false;
                const isHash = /^\$2[aby]\$.{56}$/.test(credentialData.senha);

                if (isHash) {
                    isPasswordValid = await bcrypt.compare(password, credentialData.senha);
                } else {
                    isPasswordValid = (credentialData.senha === password);
                }
                
                if (isPasswordValid && credentialData.acessoAtivo) {
                  
                    const userCredential = await signInAnonymously(auth); 
                    const firebaseUid = userCredential.user.uid;
                    
                    const municipalityPath = `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}`;
                    const municipalityDocRef = doc(db, municipalityPath);
                    const municipalitySnap = await getDoc(municipalityDocRef);
                    let serverPermissions = {};
                    let logoUrl = '';
    
                    if (municipalitySnap.exists()) {
                        const municipalityData = municipalitySnap.data();
                        serverPermissions = municipalityData.serverPortalPermissions || {};
                        logoUrl = municipalityData.logoUrl || '';
                    }

                    if (municipioId) {
                        localStorage.setItem('sisfol_municipio_persist', municipioId);
                    }
                    
                    const userData = {
                        uid: firebaseUid, name: credentialData.nome, 
                        cpf: cleanedCpf,
                        municipioId: municipioId, mustChangePassword: credentialData.primeiroAcesso || false,
                        serverPortalPermissions: serverPermissions,
                        logoUrl: logoUrl,
                    };
                    
                    // Atualização atômica
                    setUser(userData);
                    setIsAuthenticated(true);
    
                    const dict = await loadUnidadesGestoras(municipioId);
                    const vinculosPath = `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/DadosServidores`;
                    const vinculosRef = collection(db, vinculosPath);
                    const q = query(vinculosRef, where("doc_cpf", "==", cleanedCpf));
                    const querySnapshot = await getDocs(q);
                    
                    const vinculosLiberadosManual = credentialData.vinculosLiberados || [];

                    const todosOsVinculos = querySnapshot.docs
                        .map(doc => {
                            const rawData = doc.data();
                            const basicData = { 
                                id: doc.id, 
                                ...rawData, 
                                municipioId: municipioId 
                            };
                            return enrichVinculo(basicData, dict);
                        })
                        .filter(vinculo => {
                            const situacao = String(vinculo.situacao_folha || '').toUpperCase().trim();
                            const isEmFolha = situacao === 'EM FOLHA';
                            const vinculoKey = `${vinculo.matricula}-${vinculo.vinculo_id}`;
                            const isLiberadoManual = vinculosLiberadosManual.includes(vinculoKey);
                            return isEmFolha || isLiberadoManual;
                        });
    
                    if (todosOsVinculos.length === 0) {
                        await signOut(auth);
                        const errorMessage = "Nenhum vínculo ativo ou liberado encontrado para este CPF.";
                        setAuthError(errorMessage);
                        // Falha lógica, liberamos o bloqueio imediatamente
                        loginInProgress.current = false;
                        return { success: false, message: errorMessage };
                    }

                    setVinculos(todosOsVinculos);

                    const sessionData = { 
                        user: userData, 
                        vinculos: todosOsVinculos,
                    };
    
                    if (todosOsVinculos.length > 1) {
                        sessionStorage.setItem('servidorSessionData', JSON.stringify(sessionData));
                        return { success: true, mustChangePassword: userData.mustChangePassword, needsVinculoSelection: true };
                    } else {
                        const vinculoUnico = todosOsVinculos[0];
                        setVinculoSelecionado(vinculoUnico);
                        sessionData.vinculoSelecionado = vinculoUnico;

                        sessionStorage.setItem('servidorSessionData', JSON.stringify(sessionData));
                        return { success: true, mustChangePassword: userData.mustChangePassword, needsVinculoSelection: false };
                    }
                } else { 
                    const errorMessage = "Senha incorreta ou acesso inativo.";
                    setAuthError(errorMessage);
                    loginInProgress.current = false;
                    return { success: false, message: errorMessage }; 
                }
            } else { 
                const errorMessage = "CPF ou Município não encontrados.";
                setAuthError(errorMessage);
                loginInProgress.current = false;
                return { success: false, message: errorMessage }; 
            }
        } catch (error) {
            console.error("Erro no processo de login:", error);
            await signOut(auth).catch(err => console.error("Erro no signOut de limpeza:", err));
            const errorMessage = "Erro ao tentar fazer login. Tente novamente.";
            setAuthError(errorMessage);
            loginInProgress.current = false;
            return { success: false, message: errorMessage };
        } finally { 
            // 2. CORREÇÃO MOBILE: Libera o bloqueio com ATRASO (Buffer de Segurança)
            // Isso impede que o 'onAuthStateChanged' mate a sessão enquanto o mobile
            // ainda está processando o redirecionamento ou gravando no disco.
            setTimeout(() => {
                loginInProgress.current = false;
                console.log("Login manual finalizado. Buffer de segurança liberado.");
            }, 2000); 
        }
    };

    const value = {
        user,
        isAuthenticated,
        loading,
        sessionTimeLeft,
        vinculos,
        userLinks: vinculos, 
        vinculoSelecionado,
        solicitacaoConfig,
        login,
        logout,
        setPasswordChanged,
        selecionarVinculo,
        limparVinculoSelecionado,
        authError,
        clearAuthError,
    };

    return ( <AuthContext.Provider value={value}> {!loading && children} </AuthContext.Provider> );
};

export const useAuth = () => { return useContext(AuthContext); };